/**
 * Layout: header simples com identidade da sessao + tema toggle + sair.
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Captions, Film, LogOut, Captions as Logo, Sun, Moon, Monitor } from 'lucide-react';
import { clearCredenciais, getCredenciais } from '../lib/auth';
import { getTema, setTema, type Tema } from '../lib/theme';

export default function Layout() {
  const nav = useNavigate();
  const [cred, setCred] = useState(() => getCredenciais());
  const [tema, setTemaLocal] = useState<Tema>(() => getTema());

  useEffect(() => {
    const onAuthInvalida = () => {
      setCred(null);
      nav('/credenciais', { replace: true });
    };
    window.addEventListener('filmaker:auth-invalida', onAuthInvalida);
    return () => window.removeEventListener('filmaker:auth-invalida', onAuthInvalida);
  }, [nav]);

  const sair = () => {
    clearCredenciais();
    setCred(null);
    nav('/credenciais', { replace: true });
  };

  const escolherTema = (t: Tema) => {
    setTema(t);
    setTemaLocal(t);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-900 dark:text-gray-100 font-semibold">
            <Logo className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            FilmakerAI
          </Link>

          <nav className="flex items-center gap-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded ${
                  isActive
                    ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`
              }
            >
              <Captions className="w-4 h-4 inline mr-1" /> Transcrições
            </NavLink>
            <NavLink
              to="/videos"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded ${
                  isActive
                    ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`
              }
            >
              <Film className="w-4 h-4 inline mr-1" /> Vídeos
            </NavLink>
          </nav>

          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-md p-0.5" role="group" aria-label="Tema">
              <TemaBtn ativo={tema === 'light'} onClick={() => escolherTema('light')} titulo="Claro">
                <Sun className="w-3.5 h-3.5" />
              </TemaBtn>
              <TemaBtn ativo={tema === 'dark'} onClick={() => escolherTema('dark')} titulo="Escuro">
                <Moon className="w-3.5 h-3.5" />
              </TemaBtn>
              <TemaBtn ativo={tema === 'auto'} onClick={() => escolherTema('auto')} titulo="Automático (segue SO)">
                <Monitor className="w-3.5 h-3.5" />
              </TemaBtn>
            </div>
            {cred ? (
              <>
                <span title={cred.apiKey.slice(0, 12) + '...'}>
                  {cred.userEmail || cred.userId}
                </span>
                <button
                  onClick={sair}
                  className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1"
                  title="Sair"
                >
                  <LogOut className="w-3.5 h-3.5" /> sair
                </button>
              </>
            ) : (
              <Link to="/credenciais" className="text-purple-600 dark:text-purple-400 hover:underline">
                Configurar credenciais
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>
        <Outlet />
      </main>
    </div>
  );
}

function TemaBtn({ ativo, onClick, titulo, children }: {
  ativo: boolean;
  onClick: () => void;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={titulo}
      aria-pressed={ativo}
      className={`px-1.5 py-1 rounded transition-colors ${
        ativo
          ? 'bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 shadow-sm'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      }`}
    >
      {children}
    </button>
  );
}
